import { Heading } from "@/components/heading/Heading";
import { HeadingSection } from "@/components/heading/HeadingSection";
import styled from "@emotion/styled";
import {
  Icon,
  LightMode,
  Text,
  Variables,
  imageStrikethroughIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ButtonsContainer,
  CardContainer,
  FlexDiv,
  MenuButton,
  type MenuItem,
  ResponsiveButton,
} from "@repo/ui";
import { hasItems } from "@repo/utils";
import isString from "lodash/isString";
import { type ReactNode, type SVGProps } from "react";

import { type Action } from "./actionTitle/ActionTitleActions";

interface ItemCardContainerProps {
  editable?: boolean;
}

export const ItemCardContainer = styled(CardContainer)<ItemCardContainerProps>`
  padding: ${Variables.space75};
  border: ${Variables.thicknessThin} solid ${LightMode.colourNeutral3};
  display: flex;
  flex-direction: column;
  gap: ${Variables.space75};
`;

const ItemImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;

  @media (min-width: ${Variables.breakpointM}) {
    width: 64px;
    height: 64px;
  }
`;

const ImagePlaceholder = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  @media (min-width: ${Variables.breakpointM}) {
    width: 64px;
    height: 64px;
  }
`;

interface ItemCardProps {
  editable?: boolean;
  image?: string;
  imageAlt?: string;
  icon?: (prefix?: string) => SVGProps<SVGElement>[];
  hideImage?: boolean;
  amount?: string;
  addedToProjectValue?: boolean;
  details: ReactNode;
  onClick?: () => void;
  menuItems?: MenuItem[];
  actions?: Action[];
  addOn?: ReactNode;
}
interface ItemDetailsProps {
  title: string | ReactNode;
  description?: string | ReactNode;
  addOn?: ReactNode;
}

// This component follows the same design as ListViewItem
export const ItemCardDetails = ({
  title,
  description,
  addOn,
}: ItemDetailsProps) => {
  return (
    <HeadingSection>
      <FlexDiv flexDirection="column" width="100%" gap={Variables.space25}>
        <FlexDiv flexDirection="column" width="100%">
          {isString(title) ? (
            <Heading textStyle="Heading.XS">{title}</Heading>
          ) : (
            title
          )}
          {isString(description) ? (
            <Text textStyle="Body.M">{description}</Text>
          ) : (
            <div>{description}</div>
          )}
        </FlexDiv>
        {addOn}
      </FlexDiv>
    </HeadingSection>
  );
};

const Image = ({
  image,
  icon,
  alt,
}: {
  image?: string;
  icon?: (prefix?: string) => SVGProps<SVGElement>[];
  alt?: string;
}) => {
  if (image) {
    return <ItemImage src={image} alt={alt ?? ""} />;
  }

  if (icon) {
    return <Icon paths={icon} />;
  }

  return <Icon paths={imageStrikethroughIcon} />;
};

const ItemValue = ({
  amount,
  addedToProjectValue,
}: {
  amount?: string;
  addedToProjectValue?: boolean;
}) => {
  return (
    <FlexDiv flexDirection="column" alignItems="flex-end" grow>
      <Text textStyle="Heading.XS">{amount}</Text>

      {addedToProjectValue && (
        <Text textStyle="Body.S" color="neutral3" noWrap>
          {t("prompt.added_to_total_value")}
        </Text>
      )}
    </FlexDiv>
  );
};

export const ItemCard = ({
  editable,
  image,
  imageAlt,
  hideImage,
  icon,
  amount,
  addedToProjectValue,
  details,
  onClick,
  menuItems, // actions as menu lists
  actions, // actions as buttons
  addOn,
}: ItemCardProps) => {
  const showActionButtons =
    editable && (hasItems(menuItems) || hasItems(actions));

  return (
    <ItemCardContainer onClick={editable ? onClick : undefined}>
      <FlexDiv gap={Variables.space75} justifyContent="space-between">
        {hideImage !== true && (
          <ImagePlaceholder>
            <Image image={image} icon={icon} alt={imageAlt} />
          </ImagePlaceholder>
        )}

        {details}
        <ItemValue amount={amount} addedToProjectValue={addedToProjectValue} />
      </FlexDiv>
      <FlexDiv
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        gap={Variables.space200}
      >
        {addOn ?? <div />}
        {showActionButtons && (
          <ButtonsContainer justifyContent="end">
            {hasItems(menuItems) && <MenuButton menuItems={menuItems} />}
            {actions?.map((action) => (
              <ResponsiveButton
                key={`action-button__${action.id}`}
                breakpoint="xl"
                text={action.text}
                icon={action.icon}
                iconPosition={action.iconPosition}
                iconOnly={action.iconOnly}
                onClick={action.onClick}
                href={action.href}
                newWindow={action.newWindow}
                showIcon={action.showIcon ?? true}
                size="small"
                type={action.type ?? "secondary"}
                title={action.title}
                disabled={action.disabled}
              />
            ))}
          </ButtonsContainer>
        )}
      </FlexDiv>
    </ItemCardContainer>
  );
};
